import React, { useState, useRef, useCallback } from 'react';
import classNames from 'classnames';

import SearchableSelect from 'components/partials/searchable-select';
import { FlyoutArrow } from 'components/shared/side-menu/flyout-container';

import { getInitialsFromName } from 'helpers/string.helpers';

import {
  FLYOUT_POSITION,
  SIDEMENU_Z_INDEX,
} from 'components/shared/side-menu/side-menu.constants';

import Icons from 'assets/icons';

import type { SearchableSelectItem } from 'components/partials/searchable-select/searchable-select';

export interface SideMenuDropdownProps<
  TItem extends SearchableSelectItem = SearchableSelectItem,
> {
  items?: TItem[];
  selectedItem: TItem | null;
  onSelectItem: (item: TItem | null) => void;
  isCollapsed: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  notFoundMessage?: string;
  labelKey?: string | ((item: TItem) => string);
  valueKey?: string;
}

const SideMenuDropdownImpl = ({
  items = [],
  selectedItem,
  onSelectItem,
  isCollapsed,
  isLoading = false,
  disabled = false,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No items available',
  notFoundMessage = 'No items found',
  labelKey = 'name',
  valueKey = 'id',
}: SideMenuDropdownProps<SearchableSelectItem>): React.JSX.Element => {
  const [flyoutTop, setFlyoutTop] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const updateFlyoutPosition = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();

      setFlyoutTop(rect.top);
    }
  }, []);

  const handleToggle = useCallback(
    (originalToggle: () => void) => () => {
      updateFlyoutPosition();
      originalToggle();
    },
    [updateFlyoutPosition]
  );

  const flyoutLeft = isCollapsed
    ? FLYOUT_POSITION.collapsedLeft
    : FLYOUT_POSITION.expandedLeft;

  const renderFlyoutPanel = useCallback(
    ({ panelContent }: { panelContent: React.ReactNode }) => (
      <>
        <div
          className="fixed h-10"
          style={{
            left: FLYOUT_POSITION.bridgeLeft,
            width: FLYOUT_POSITION.bridgeWidth,
            top: flyoutTop,
            zIndex: SIDEMENU_Z_INDEX.flyoutBridge,
          }}
        />
        <div
          className="fixed flex items-start"
          style={{
            left: flyoutLeft,
            top: flyoutTop,
            zIndex: SIDEMENU_Z_INDEX.flyout,
          }}
        >
          <FlyoutArrow />
          <div className="bg-white rounded-xl border border-navy-200 w-[280px] -ml-px max-h-[400px] flex flex-col">
            {panelContent}
          </div>
        </div>
      </>
    ),
    [flyoutLeft, flyoutTop]
  );

  const renderCollapsedTrigger = useCallback(
    ({
      selectedItem,
      selectedLabel,
      handleToggle: originalToggle,
    }: {
      selectedItem: Record<string, unknown> | null;
      selectedLabel: string;
      handleToggle: () => void;
    }) => (
      <button
        disabled={disabled}
        onClick={handleToggle(originalToggle)}
        className={classNames(
          'w-10 h-10 rounded-xl flex items-center justify-center text-sm font-medium transition-colors border',
          {
            'bg-white text-gray-700 border-navy-200 hover:bg-navy-50 hover:border-gray-600':
              selectedItem && !disabled,
            'bg-navy-50 text-gray-600 border-dashed border-navy-200 hover:border-gray-600':
              !selectedItem && !disabled,
            'cursor-not-allowed opacity-50': disabled,
          }
        )}
        title={selectedLabel || placeholder}
        aria-label={selectedLabel || placeholder}
      >
        {selectedItem ? (
          getInitialsFromName(selectedLabel)
        ) : (
          <Icons.Plus className="size-3.5" />
        )}
      </button>
    ),
    [disabled, placeholder, handleToggle]
  );

  const renderExpandedTrigger = useCallback(
    ({
      selectedItem,
      selectedLabel,
      handleToggle: originalToggle,
      handleClear,
    }: {
      selectedItem: Record<string, unknown> | null;
      selectedLabel: string;
      handleToggle: () => void;
      handleClear: () => void;
    }) => (
      <div
        className={classNames(
          'w-full flex items-center justify-between px-3 py-2 border rounded-lg transition-colors text-sm border-navy-200',
          {
            'cursor-not-allowed opacity-50': disabled,
          }
        )}
      >
        <button
          disabled={disabled}
          onClick={handleToggle(originalToggle)}
          className="truncate flex-1 text-left bg-transparent border-none cursor-pointer p-0 text-inherit"
          aria-label={selectedLabel || placeholder}
        >
          <span
            className={classNames({
              'text-gray-700 font-medium': selectedItem,
              'text-gray-600': !selectedItem,
            })}
            title={selectedLabel || placeholder}
          >
            {selectedLabel || placeholder}
          </span>
        </button>

        <div className="flex items-center gap-1">
          {selectedItem && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 min-w-[24px] min-h-[24px] flex items-center justify-center hover:bg-gray-100 rounded transition-colors text-gray-600 hover:text-gray-700 bg-transparent border-none cursor-pointer"
              aria-label="Clear selection"
            >
              <Icons.Close className="size-3.5" aria-hidden="true" />
            </button>
          )}
          <div className="flex flex-col text-gray-600">
            <Icons.Arrow customClass="size-2.5 -rotate-90" />
            <Icons.Arrow customClass="size-2.5 rotate-90 -mt-0.5" />
          </div>
        </div>
      </div>
    ),
    [disabled, placeholder, handleToggle]
  );

  const containerClassName = isCollapsed
    ? 'px-2 py-3 flex justify-center relative'
    : 'px-3 py-2 relative';

  return (
    <div ref={containerRef} className={containerClassName}>
      <SearchableSelect
        items={items}
        selectedItem={selectedItem}
        onSelect={onSelectItem}
        isLoading={isLoading}
        disabled={disabled}
        searchPlaceholder={searchPlaceholder}
        emptyMessage={emptyMessage}
        notFoundMessage={notFoundMessage}
        labelKey={labelKey}
        valueKey={valueKey}
        renderTrigger={isCollapsed ? renderCollapsedTrigger : renderExpandedTrigger}
        renderPanel={renderFlyoutPanel}
      />
    </div>
  );
};

const SideMenuDropdown = SideMenuDropdownImpl as unknown as (<
  TItem extends SearchableSelectItem = SearchableSelectItem,
>(
  props: SideMenuDropdownProps<TItem>
) => React.ReactElement);

export { SideMenuDropdown };
export default SideMenuDropdown;
